// Analytics utilities for GTM and GA4 tracking
import { TokenPackage } from '@/components/tokens/constants';

// Declare global dataLayer for GTM
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// GA4 Event interfaces
interface GA4BeginCheckoutEvent {
  event: 'begin_checkout';
  ecommerce: {
    currency: string;
    value: number;
    items: Array<{
      item_id: string;
      item_name: string;
      item_category: string;
      item_variant: string;
      price: number;
      quantity: number;
    }>;
  };
}

interface GA4SignUpEvent {
  event: 'sign_up';
  method: string;
  user_id?: string;
}

// Helper to ensure dataLayer exists
const getDataLayer = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }
  return [];
};

// Convert TokenPackage to GA4 ecommerce format
export const mapTokenPackageToGA4 = (pkg: TokenPackage) => {
  const variantMap: Record<string, string> = {
    'starter': 'Basic',
    'popular': 'Popular', 
    'best-value': 'Best Value'
  };

  return {
    item_id: `tokens_${pkg.id}`,
    item_name: `${(pkg.tokens / 1000).toFixed(0)}K Tokens`,
    item_category: 'Digital Tokens',
    item_variant: variantMap[pkg.id] || 'Standard',
    price: pkg.price,
    quantity: 1
  };
};

// Track begin_checkout event
export const trackBeginCheckout = (pkg: TokenPackage) => {
  try {
    const dataLayer = getDataLayer();
    const eventData: GA4BeginCheckoutEvent = {
      event: 'begin_checkout',
      ecommerce: {
        currency: 'USD',
        value: pkg.price,
        items: [mapTokenPackageToGA4(pkg)]
      }
    };

    dataLayer.push(eventData);
    console.log('🛒 Analytics: Begin checkout tracked', eventData);
  } catch (error) {
    console.error('❌ Analytics: Failed to track begin_checkout', error);
  }
};

// Track sign_up event with deduplication
export const trackSignUp = (method: string, userId?: string) => {
  try {
    // Check if we've already tracked this user's signup
    const storageKey = `signup_tracked_${userId || 'guest'}`;
    if (userId && localStorage.getItem(storageKey)) {
      console.log('📝 Analytics: Sign up already tracked for this user');
      return;
    }

    const dataLayer = getDataLayer();
    const eventData: GA4SignUpEvent = {
      event: 'sign_up',
      method,
      ...(userId && { user_id: userId })
    };

    dataLayer.push(eventData);
    
    // Mark this user as tracked
    if (userId) {
      localStorage.setItem(storageKey, 'true');
    }
    
    console.log('📝 Analytics: Sign up tracked', eventData);
  } catch (error) {
    console.error('❌ Analytics: Failed to track sign_up', error);
  }
};

// Detect auth method from user metadata or context
export const detectAuthMethod = (user: any): string => {
  if (user?.app_metadata?.provider) {
    return user.app_metadata.provider;
  }
  if (user?.app_metadata?.providers?.includes('google')) {
    return 'google';
  }
  if (user?.app_metadata?.providers?.includes('discord')) {
    return 'discord';
  }
  return 'email';
};

// Check if user is newly registered (within last 5 seconds)
export const isNewUser = (userCreatedAt: string): boolean => {
  const now = new Date().getTime();
  const userCreated = new Date(userCreatedAt).getTime();
  const timeDiff = now - userCreated;
  
  // Consider "new" if created within last 5 seconds
  return timeDiff < 5000;
};